import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useDemoMode } from '@/contexts/DemoModeContext';
import { useDemoStore } from '@/contexts/DemoStoreContext';
import { useAuth } from '@/hooks/useAuth';
// DEMO-4 — the `mockClients` fallback (Elevate Fitness Co., Aurora
// Cosmetics, "Marcus & Elena", all created January–March 2024) is
// gone. `demoClients` in the overlay is the only demo roster, and the
// store is hydrated from it, so there is no path back to 2024 names.
import { demoClients } from '@/data/demoOverlay';
import type { Tables, TablesInsert } from '@/integrations/supabase/types';
import { debug } from '@/lib/debug';
import { useToast } from '@/hooks/use-toast';
import { useNovaEmotion } from '@/components/nova/NovaEmotion';
import { track } from '@/lib/analytics';

export type Client = Tables<'clients'>;
export type ClientInsert = TablesInsert<'clients'>;

/**
 * PLANS-1B — typed error thrown when the org's Starter-tier client cap
 * (15) is reached. Caught by client-create surfaces to render an
 * inline `UpgradeNudgeCard(reason: 'client_cap')` instead of a red toast.
 *
 * Relies on the `can_add_client(org_id)` RPC added in PLANS-1A. If the
 * RPC returns { allowed: false, current, cap }, we throw this; the
 * insert never happens.
 */
export class ClientCapReachedError extends Error {
  code = 'client_cap_reached' as const;
  current: number;
  cap: number;
  constructor(current: number, cap: number) {
    super(
      `You've reached your Orbit Starter client limit (${current} of ${cap}). Upgrade to Orbit for unlimited clients.`,
    );
    this.name = 'ClientCapReachedError';
    this.current = current;
    this.cap = cap;
  }
}

export const useClients = () => {
  const { isDemo } = useDemoMode();
  const { user } = useAuth();
  const demoStore = useDemoStore();

  return useQuery({
    queryKey: ['clients', isDemo, user?.id, isDemo ? demoStore.version : 0],
    queryFn: async () => {
      if (isDemo) {
        // Prefer the session-scoped store so newly-added Preview
        // Studio clients show up in the list without a refresh.
        // Falls back to the overlay seed only if the store is empty
        // (e.g. tests mounting outside the provider).
        const rows = demoStore.clients.list();
        return rows.length ? (rows as unknown as Client[]) : (demoClients as unknown as Client[]);
      }
      
      // First get user's org_id from profile
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('org_id')
        .eq('id', user!.id)
        .maybeSingle();
      
      if (profileError) throw profileError;
      if (!profile?.org_id) return [];
      
      // Then fetch clients for that org
      const { data, error } = await supabase
        .from('clients')
        .select('*')
        .eq('org_id', profile.org_id)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data;
    },
    enabled: !!user || isDemo,
  });
};

export const useClient = (clientId: string | undefined) => {
  const { isDemo } = useDemoMode();
  const { user } = useAuth();
  const demoStore = useDemoStore();

  return useQuery({
    queryKey: ['client', clientId, isDemo, isDemo ? demoStore.version : 0],
    queryFn: async () => {
      if (!clientId) return null;

      if (isDemo) {
        const rows = demoStore.clients.list();
        return (rows.find((c) => c.id === clientId) as unknown as Client)
          ?? (demoClients.find((c) => c.id === clientId) as unknown as Client)
          ?? null;
      }
      
      const { data, error } = await supabase
        .from('clients')
        .select('*')
        .eq('id', clientId)
        .maybeSingle();
      
      if (error) throw error;
      return data;
    },
    enabled: (!!user || isDemo) && !!clientId,
  });
};

export const useCreateClient = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { trigger: triggerNova } = useNovaEmotion();
  const { isDemo } = useDemoMode();
  const demoStore = useDemoStore();

  return useMutation({
    mutationFn: async (client: ClientInsert & { phone?: string | null; category_id?: string | null }) => {
      if (isDemo) {
        const now = new Date().toISOString();
        const created = {
          id: `demo-c-${crypto.randomUUID().slice(0, 8)}`,
          created_at: now,
          updated_at: now,
          ...client,
        };
        return demoStore.clients.add(created) as unknown as Client;
      }

      debug.log('Creating client', { name: client.name, orgId: client.org_id });

      // PLANS-1B — gate the insert on `can_add_client()` when we know
      // the org id. Starter caps at 15 clients; the RPC returns
      // `{ allowed, current, cap }`. If disallowed, surface a typed
      // error so the caller can render an UpgradeNudgeCard instead of
      // a generic destructive toast.
      //
      // FIX-8 — two fixes to the original wiring:
      //   1. The RPC signature is `can_add_client(p_org_id uuid)`
      //      (migration 20260729230600); we passed `{ org_id }` behind
      //      an `as any`, so PostgREST rejected every call…
      //   2. …and the catch-all swallowed that error and let the
      //      insert proceed, which meant the cap was NEVER enforced.
      // The guard now FAILS CLOSED: any gate error blocks the insert
      // with a loud toast (via the mutation's onError) instead of
      // silently skipping the check.
      if (client.org_id) {
        const gate = await supabase.rpc('can_add_client', {
          p_org_id: client.org_id,
        });
        if (gate.error) {
          debug.error(
            { action: 'can_add_client', payload: { org_id: client.org_id } },
            gate.error,
          );
          throw new Error(
            "Couldn't verify your plan's client limit, so nothing was added. Try again in a moment.",
          );
        }
        const payload = gate.data as unknown as
          | { allowed?: boolean; current?: number; cap?: number }
          | null;
        if (payload && payload.allowed === false) {
          const current = payload.current ?? 0;
          const cap = payload.cap ?? 15;
          track('client_limit_reached', {
            surface: 'clients_create',
            current,
            cap,
          });
          throw new ClientCapReachedError(current, cap);
        }
      }

      const { data, error } = await supabase
        .from('clients')
        .insert(client as any)
        .select()
        .single();

      if (error) {
        debug.error({ action: 'create_client', payload: client }, error);
        throw error;
      }
      debug.success('Client created', { id: data.id });
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      // Nova celebrates new clients — a quick reaction across the whole app.
      triggerNova('celebrating');
    },
    onError: (error: Error) => {
      // PLANS-1B — swallow cap errors here so the destructive toast
      // doesn't fire on top of the caller's inline UpgradeNudgeCard.
      // The caller is responsible for surfacing the nudge.
      if (error instanceof ClientCapReachedError) return;
      toast({
        title: 'Error creating client',
        description: debug.parseSupabaseError(error),
        variant: 'destructive',
      });
    },
  });
};

export const useUpdateClient = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { isDemo } = useDemoMode();
  const demoStore = useDemoStore();

  return useMutation({
    mutationFn: async ({ id, ...updates }: Partial<Client> & { id: string }) => {
      if (isDemo) {
        const merged = demoStore.clients.update(id, updates as any);
        return (merged ?? { id, ...updates }) as unknown as Client;
      }

      debug.log('Updating client', { id, fields: Object.keys(updates) });
      const { data, error } = await supabase
        .from('clients')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        debug.error({ action: 'update_client', payload: { id, ...updates } }, error);
        throw error;
      }
      debug.success('Client updated', { id });
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      queryClient.invalidateQueries({ queryKey: ['client', data.id] });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error updating client',
        description: debug.parseSupabaseError(error),
        variant: 'destructive',
      });
    },
  });
};

export const useDeleteClient = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { isDemo } = useDemoMode();
  const demoStore = useDemoStore();

  return useMutation({
    mutationFn: async (id: string) => {
      if (isDemo) {
        demoStore.clients.remove(id);
        return;
      }

      debug.log('Deleting client', { id });
      const { error } = await supabase
        .from('clients')
        .delete()
        .eq('id', id);

      if (error) {
        debug.error({ action: 'delete_client', payload: { id } }, error);
        throw error;
      }
      debug.success('Client deleted', { id });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error deleting client',
        description: debug.parseSupabaseError(error),
        variant: 'destructive',
      });
    },
  });
};

/**
 * Subscribe to row-level changes on the `clients` table and invalidate
 * the local query cache when anything happens. Mount this on any page
 * that displays clients so a row added on iOS / another browser / the
 * desktop CRM all refresh within a Realtime tick.
 *
 * Also fires Nova's celebrating animation when a NEW client arrives
 * from another device — gives the studio a tactile "you got mail"
 * moment across platforms.
 */
export const useClientsRealtime = () => {
  const queryClient = useQueryClient();
  const { trigger: triggerNova } = useNovaEmotion();
  const { user } = useAuth();

  useEffect(() => {
    if (!user) return;
    const channel = supabase
      .channel('clients-live-web')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'clients' },
        () => {
          queryClient.invalidateQueries({ queryKey: ['clients'] });
          triggerNova('celebrating', { duration: 3000 });
        },
      )
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'clients' },
        () => queryClient.invalidateQueries({ queryKey: ['clients'] }),
      )
      .on(
        'postgres_changes',
        { event: 'DELETE', schema: 'public', table: 'clients' },
        () => queryClient.invalidateQueries({ queryKey: ['clients'] }),
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, queryClient, triggerNova]);
};
